import { weakNormalizeSemantic } from "./normalizeSemantic";
import {
  allUnitsAreSi,
  nodeToSemantic,
  semanticHasFloat,
  semanticToSympyCode,
  SympyBuildError,
} from "./sympy-builder";
import { buildCustomUnitPreamble } from "./unitToSympyMap";

import type { SemanticExpr } from "./types";
import type { NodeJson } from "@corca/data";

export type SympyCommand =
  | "expand"
  | "solve"
  | "simplify"
  | "apart"
  | "factor"
  | "evaluate"
  | "convert";

/**
 * Trig classification flags supplied by the caller. The caller is responsible
 * for inspecting the input NodeJson tree and reporting whether the expression
 * contains a trig function whose argument is a bare numeric literal (no variables,
 * constants, or units). See detectTrigFlags in getPhantomHints.ts.
 */
export type TrigFlags = {
  hasBareNumericTrigArg: boolean;
};

export type TaylorSeriesOptions = {
  variableName: string;
  center: number;
  order: number;
};

export type FunctionDef = {
  paramSymbols: string[];
  paramNames: string[];
  bodyExpr: string;
};

export type BuildSympyCodeOptions = {
  solveVariable?: string;
  substitutions?: Map<string, string>;
  functionDefs?: Map<string, FunctionDef>;
  indexedBases?: Set<string>;
  trigFlags?: TrigFlags;
  domain?: string;
  scalarRatioDivisor?: number | null;
  convertTargetExpr?: string;
};

/** Throws SympyBuildError when the command and options don't describe a runnable request. */
export function buildSympyCode(
  command: SympyCommand,
  expr: string,
  variables: Map<string, string>,
  options: BuildSympyCodeOptions = {},
): { expr: string } {
  const {
    solveVariable,
    substitutions,
    functionDefs,
    indexedBases,
    trigFlags,
    domain,
    scalarRatioDivisor,
    convertTargetExpr,
  } = options;

  if (solveVariable && !variables.has(solveVariable)) {
    throw new SympyBuildError(
      `Cannot solve for unknown variable: ${solveVariable} (not found in [${[...variables.keys()].join(", ")}])`,
    );
  }

  if (command === "convert" && !convertTargetExpr) {
    throw new SympyBuildError("convert requires a target unit");
  }

  const codeLines = ["import sympy as sp", "import sympy_json", "import sympy_json.statistics"];

  const needsUnits =
    expr.includes("units.") ||
    Boolean(convertTargetExpr?.includes("units.")) ||
    (substitutions && [...substitutions.values()].some((v) => v.includes("units."))) ||
    (functionDefs && [...functionDefs.values()].some((fd) => fd.bodyExpr.includes("units.")));

  if (needsUnits) {
    codeLines.push("import sympy.physics.units as units");
    codeLines.push("from sympy.physics.units.util import convert_to");
    codeLines.push(
      ...buildCustomUnitPreamble(
        expr,
        convertTargetExpr ?? "",
        ...[...(substitutions?.values() ?? [])],
        ...[...(functionDefs?.values() ?? [])].map((fd) => fd.bodyExpr),
      ),
    );
  }
  let solveVar = "var0";

  for (const [varName, varSymbol] of variables.entries()) {
    const declarator = indexedBases?.has(varName) ? "sp.IndexedBase" : "sp.Symbol";
    if (varName === solveVariable) {
      codeLines.push(`${varSymbol} = ${declarator}('${varName}')`);
      solveVar = varSymbol;
    } else if (!substitutions?.has(varName)) {
      codeLines.push(`${varSymbol} = ${declarator}('${varName}')`);
    }
  }

  if (substitutions) {
    for (const [varName, sympyExpr] of substitutions.entries()) {
      if (varName !== solveVariable) {
        const varSymbol = variables.get(varName);
        if (varSymbol) {
          codeLines.push(`${varSymbol} = ${sympyExpr}`);
        }
      }
    }
  }

  if (functionDefs) {
    for (const [funcName, { paramSymbols, paramNames, bodyExpr }] of functionDefs.entries()) {
      const funcVarSymbol = variables.get(funcName);
      if (funcVarSymbol) {
        for (let i = 0; i < paramSymbols.length; i++) {
          codeLines.push(`${paramSymbols[i]} = sp.Symbol('${paramNames[i]}')`);
        }
        const paramList =
          paramSymbols.length === 1 ? paramSymbols[0] : `(${paramSymbols.join(", ")})`;
        codeLines.push(`${funcVarSymbol} = sp.Lambda(${paramList}, ${bodyExpr})`);
      }
    }
  }

  const isSet = /^sp\.(Union|Intersection|Complement|FiniteSet)\b/.test(expr);
  const isInequality = /^sp\.(StrictLessThan|StrictGreaterThan|LessThan|GreaterThan)\b/.test(expr);

  const hasDegreeUnit =
    expr.includes("units.degree") ||
    (substitutions && [...substitutions.values()].some((v) => v.includes("units.degree"))) ||
    (functionDefs && [...functionDefs.values()].some((fd) => fd.bodyExpr.includes("units.degree")));

  codeLines.push(`expr = ${expr}`);
  // convert_to handles degree↔radian natively; the pi/180 substitution would strip the unit
  if (hasDegreeUnit && command !== "convert") {
    codeLines.push("expr = expr.subs(units.degree, sp.pi/180)");
  }

  if (isSet) {
    codeLines.push("res = expr.doit()");
  } else if (trigFlags?.hasBareNumericTrigArg) {
    codeLines.push(`res = sp.N(expr)`);
  } else {
    switch (command) {
      case "expand": {
        codeLines.push(`res = sp.expand(expr)`);
        break;
      }
      case "simplify": {
        codeLines.push(`res = sp.simplify(expr)`);
        break;
      }
      case "solve": {
        if (solveVariable && domain) {
          codeLines.push(`res = sympy_json.solveset(expr, ${solveVar}, ${domain})`);
        } else if (isInequality && variables.size > 0) {
          const ineqVar = solveVariable ? solveVar : [...variables.values()][0];
          codeLines.push(`res = sp.solveset(expr, ${ineqVar}, domain=sp.S.Reals)`);
        } else if (solveVariable) {
          codeLines.push(`res = sympy_json.solve(expr, ${solveVar})`);
        } else if (variables.size === 0) {
          codeLines.push(`res = expr.doit()`);
        } else {
          codeLines.push(`res = sympy_json.solve_or_evaluate(expr, ${solveVar})`);
        }
        break;
      }
      case "apart": {
        codeLines.push(`res = sp.apart(expr)`);
        break;
      }
      case "factor": {
        codeLines.push(`res = sp.factor(expr)`);
        break;
      }
      case "evaluate": {
        codeLines.push(`res = expr.doit()`);
        break;
      }
      case "convert": {
        codeLines.push(`res = sp.N(convert_to(expr, ${convertTargetExpr}))`);
        break;
      }
    }
  }

  // Simplify compound SI units into derived units (e.g. kg·m/s² → N).
  // Skipped for convert: it would re-collapse the user's chosen target unit.
  if (needsUnits && command !== "convert") {
    codeLines.push(
      "res = [sympy_json.simplify_units(r) for r in res] if isinstance(res, list) else sympy_json.simplify_units(res)",
    );
  }

  // Scalar ratio survived propagation: the value is a fraction of 1 (e.g. 29/100); rescale by
  // the divisor (29/100 → 29) so the re-attached ratio suffix (%, ‰, ‱) reads naturally.
  if (scalarRatioDivisor) {
    codeLines.push(
      `res = [r * ${scalarRatioDivisor} for r in res] if isinstance(res, list) else res * ${scalarRatioDivisor}`,
    );
  }

  codeLines.push("sympy_json.print_sympy(res)");

  return { expr: codeLines.join("\n") };
}

/** Throws SympyBuildError when the series variable is not one of the expression's variables. */
export function buildTaylorSeriesCode(
  expr: string,
  variables: Map<string, string>,
  { variableName, center, order }: TaylorSeriesOptions,
): { expr: string } {
  const variableSymbol = variables.get(variableName);

  if (!variableSymbol) {
    throw new SympyBuildError(`Cannot build Taylor series for unknown variable: ${variableName}`);
  }

  const codeLines = ["import sympy as sp", "import sympy_json", "import sympy_json.statistics"];

  for (const [varName, varSymbol] of variables.entries()) {
    codeLines.push(`${varSymbol} = sp.Symbol('${varName}')`);
  }

  codeLines.push(`expr = ${expr}`);
  codeLines.push(`center = sp.Rational(${JSON.stringify(center)})`);
  codeLines.push(`res = expr.series(${variableSymbol}, center, ${order})`);
  codeLines.push("sympy_json.print_sympy(res)");

  return { expr: codeLines.join("\n") };
}

export type SequenceOptions = {
  indexSymbol: string;
  lo: number;
  hi: number;
  substitutions?: Map<string, string>;
};

// Mirrors buildSympyCode's eager-substitution / lazy-expression layout so the two code
// generators stay consistent if that split is ever revisited.
export function buildSequenceSympyCode(
  bodyExpr: string,
  variables: Map<string, string>,
  { indexSymbol, lo, hi, substitutions }: SequenceOptions,
): { expr: string } {
  const codeLines = ["import sympy as sp", "import sympy_json"];

  for (const [varName, varSymbol] of variables.entries()) {
    if (!substitutions?.has(varName)) {
      codeLines.push(`${varSymbol} = sp.Symbol('${varName}')`);
    }
  }

  if (substitutions) {
    for (const [varName, sympyExpr] of substitutions.entries()) {
      const varSymbol = variables.get(varName);
      if (varSymbol) {
        codeLines.push(`${varSymbol} = ${sympyExpr}`);
      }
    }
  }

  codeLines.push(`expr = ${bodyExpr}`);
  codeLines.push(`res = sympy_json.sequence(expr, ${indexSymbol}, ${lo}, ${hi})`);
  codeLines.push("sympy_json.print_sympy(res)");

  return { expr: codeLines.join("\n") };
}

/** Throws SympyBuildError for input it cannot translate; the host decides how to report that. */
export function parseNodeToSympy(
  node: NodeJson,
  existingVariables?: Map<string, string>,
  /** Override SI-unit emission. `false` = force non-SI path, `null`/`undefined` = auto-detect. */
  emitSiUnitsOverride?: boolean | null,
): {
  expr: string;
  semantic: SemanticExpr;
  variables: Map<string, string>;
  symbolNodes: Map<string, NodeJson>;
  indexedBases: Set<string>;
  hasDecimal: boolean;
  warnings: string[];
} {
  const effectiveOverride = emitSiUnitsOverride ?? allUnitsAreSi(node);
  const parsed = nodeToSemantic(node, existingVariables, effectiveOverride);

  return {
    expr: semanticToSympyCode(parsed.expr, parsed.variables),
    semantic: weakNormalizeSemantic(parsed.expr),
    variables: parsed.variables,
    symbolNodes: parsed.symbolNodes,
    indexedBases: parsed.indexedBases,
    hasDecimal: semanticHasFloat(parsed.expr),
    warnings: parsed.warnings,
  };
}

/** Throws SympyBuildError for input it cannot translate or build a runnable request from. */
export function parseAndBuildSympyCode(
  command: SympyCommand,
  node: NodeJson,
  solveVariable?: string,
): {
  expr: string;
  variables: Map<string, string>;
  symbolNodes: Map<string, NodeJson>;
  warnings: string[];
} {
  const parsed = parseNodeToSympy(node);

  if (!parsed.expr) {
    throw new SympyBuildError("Empty expression");
  }

  return {
    expr: buildSympyCode(command, parsed.expr, parsed.variables, {
      solveVariable,
      indexedBases: parsed.indexedBases,
    }).expr,
    variables: parsed.variables,
    symbolNodes: parsed.symbolNodes,
    warnings: parsed.warnings,
  };
}

/**
 * Solve a system of equations for several variables at once — the `(x, y) =` hint, where the
 * equations come from earlier lines rather than from the expression itself.
 */
export function buildSystemSolveSympyCode(
  equations: string[],
  solveVarSymbols: string[],
  variables: Map<string, string>,
  substitutions: Map<string, string>,
): string {
  const codeLines = ["import sympy as sp", "import sympy_json", "import sympy_json.statistics"];

  // Declare symbols
  for (const [varName, varSymbol] of variables.entries()) {
    if (!substitutions.has(varName)) {
      codeLines.push(`${varSymbol} = sp.Symbol('${varName}')`);
    }
  }

  // Apply substitutions
  for (const [varName, sympyExpr] of substitutions.entries()) {
    const varSymbol = variables.get(varName);
    if (varSymbol) {
      codeLines.push(`${varSymbol} = ${sympyExpr}`);
    }
  }

  // Build the system of equations
  codeLines.push(`equations = [${equations.join(", ")}]`);

  // Solve the system
  codeLines.push(`res = sympy_json.solve(equations, [${solveVarSymbols.join(", ")}])`);

  codeLines.push("sympy_json.print_sympy(res)");

  return codeLines.join("\n");
}
